import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, MapPin, Route as RouteIcon, Save, X, Search, Circle, Square, Triangle, Pencil } from 'lucide-react'
import { apiClient } from '../config/api'
import CorridorMap from './CorridorMap'

interface Corridor {
  id: string
  name: string
  description?: string
  type: 'trajectory' | 'geofence'
  points: Array<{
    lat: number
    lng: number
    address?: string
  }>
  color: string
  width: number
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

// Enhanced geofence type definitions
export type GeofenceShapeType = 'circle' | 'square' | 'triangle' | 'polygon' | 'point'

export type GeofencePlaceType = 
  | 'gas_station' | 'parking' | 'office' | 'warehouse' 
  | 'border' | 'port' | 'airport' | 'bridge'
  | 'police' | 'control_point' | 'security_zone'
  | 'custom'

export type GeofencePurpose = 
  | 'avoid' | 'mandatory' | 'checkpoint'
  | 'authorized' | 'restricted' | 'forbidden'
  | 'tracking'

export interface GeofenceParameters {
  // Alert Settings (Universal - applies to any geofence)
  alertOnEntry: boolean
  alertOnExit: boolean
  alertMessage?: string
  
  // Speed Control
  speedLimit?: number
  speedAlert?: boolean
  
  // Authorization
  authorizationLevel: 'public' | 'restricted' | 'private'
  allowedVehicleTypes?: string[]
  requiredPermits?: string[]
  
  // Time Restrictions
  timeRestrictions?: {
    allowedHours?: string[]
    allowedDays?: string[]
  }
  
  // Monitoring
  dwellTimeLimit?: number
  customProperties?: {[key: string]: any}
}

interface Geofence {
  id: string
  name: string
  description?: string
  type: GeofenceShapeType
  placeType: GeofencePlaceType
  purpose: GeofencePurpose
  parameters: GeofenceParameters
  center?: { lat: number, lng: number }
  radius?: number // for circles
  bounds?: Array<{ lat: number, lng: number }> // for polygons
  color: string
  fillColor: string
  isActive: boolean
  alertOnEntry: boolean
  alertOnExit: boolean
  createdAt: Date
  updatedAt: Date
}

interface CorridorManagerProps {
  onCorridorSelect?: (corridor: Corridor) => void
  onCorridorsChange?: (corridors: Corridor[]) => void
  onGeofenceSelect?: (geofence: Geofence) => void
  onStartDrawing?: (type: 'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point') => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
  creationRequest?: CorridorCreationRequest | null
}

export interface CorridorCreationRequest {
  requestId: number
  mode: 'geofence' | 'trajectory'
  point: { lat: number, lng: number }
  zoom: number
  referenceTrajectory: Array<{ lat: number, lng: number }>
  dateRange: { startDate: string, endDate: string }
  vehicle: {
    id: string
    source?: 'ASSET' | 'CONTAINER'
    assetType?: string
    trackingStatus?: 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'
    heading?: number
  }
  vehicleLabel: string
}

const CorridorManager: React.FC<CorridorManagerProps> = ({
  onCorridorSelect,
  onCorridorsChange,
  onGeofenceSelect,
  onStartDrawing,
  showMessage,
  showConfirm,
  creationRequest
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'trajectories' | 'geofences'>('trajectories')
  const [corridors, setCorridors] = useState<Corridor[]>([])
  const [geofences, setGeofences] = useState<Geofence[]>([])
  const [selectedCorridor, setSelectedCorridor] = useState<Corridor | null>(null)
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [editForm, setEditForm] = useState<Partial<Corridor>>({})
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [drawingType, setDrawingType] = useState<'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point'>('trajectory')
  const [centerOnCorridor, setCenterOnCorridor] = useState(false)
  const [centerOnGeofence, setCenterOnGeofence] = useState(false)
  const [isEditingGeofence, setIsEditingGeofence] = useState(false)
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null)
  const [pendingCreation, setPendingCreation] = useState<CorridorCreationRequest | null>(null)
  const [pendingFocus, setPendingFocus] = useState<{ zoneType: 'geofence' | 'corridor', zoneId: string } | null>(null)

  // Load corridors and geofences from database
  useEffect(() => {
    loadCorridorsFromDatabase()
    loadGeofencesFromDatabase()
  }, [])

  useEffect(() => {
    const focusZone = (event: Event) => {
      const detail = (event as CustomEvent<{ zoneType?: 'geofence' | 'corridor', zoneId?: string }>).detail
      if (!detail?.zoneId || !['geofence', 'corridor'].includes(String(detail.zoneType))) return
      setPendingFocus({ zoneType: detail.zoneType!, zoneId: String(detail.zoneId) })
    }
    window.addEventListener('fleet:corridor-focus', focusZone)
    return () => window.removeEventListener('fleet:corridor-focus', focusZone)
  }, [])

  useEffect(() => {
    if (!pendingFocus) return
    setIsDrawingMode(false)
    setPendingCreation(null)
    if (pendingFocus.zoneType === 'geofence') {
      const match = geofences.find(zone => String(zone.id) === pendingFocus.zoneId)
      if (!match) return
      setActiveTab('geofences')
      setSelectedCorridor(null)
      setSelectedGeofence(match)
      setCenterOnGeofence(true)
      window.setTimeout(() => setCenterOnGeofence(false), 100)
    } else {
      const match = corridors.find(zone => String(zone.id) === pendingFocus.zoneId)
      if (!match) return
      setActiveTab('trajectories')
      setSelectedGeofence(null)
      setSelectedCorridor(match)
      setCenterOnCorridor(true)
      window.setTimeout(() => setCenterOnCorridor(false), 100)
    }
    setPendingFocus(null)
  }, [pendingFocus, geofences, corridors])

  useEffect(() => {
    if (!creationRequest) return
    setIsEditing(false)
    setIsCreating(false)
    setSelectedCorridor(null)
    setIsEditingGeofence(false)
    setEditingGeofence(null)
    setSelectedGeofence(null)
    setPendingCreation(creationRequest)
    setActiveTab(creationRequest.mode === 'geofence' ? 'geofences' : 'trajectories')
    setDrawingType(creationRequest.mode === 'geofence' ? 'circle' : 'trajectory')
    setIsDrawingMode(true)
  }, [creationRequest])

  // Vehicle Control Center deep link. It opens the existing drawing workflow
  // without manufacturing a map position or changing the normal map flow.
  useEffect(() => {
    const openCreation = (event: Event) => {
      const mode = (event as CustomEvent<{ mode?: 'geofence' | 'trajectory' }>).detail?.mode
      if (mode !== 'geofence' && mode !== 'trajectory') return
      setIsEditing(false)
      setIsCreating(false)
      setSelectedCorridor(null)
      setIsEditingGeofence(false)
      setEditingGeofence(null)
      setSelectedGeofence(null)
      setPendingCreation(null)
      setActiveTab(mode === 'geofence' ? 'geofences' : 'trajectories')
      setDrawingType(mode === 'geofence' ? 'circle' : 'trajectory')
      setIsDrawingMode(true)
    }
    window.addEventListener('fleet:corridor-create', openCreation)
    return () => window.removeEventListener('fleet:corridor-create', openCreation)
  }, [])


  const loadCorridorsFromDatabase = async () => {
    try {
      console.log('🛣️ Loading corridors from database...')
      const response = await apiClient.get('/corridors')
      
      if (response.data && Array.isArray(response.data)) {
        const dbCorridors: Corridor[] = response.data.map(corridor => ({
          id: corridor.id,
          name: corridor.name,
          description: corridor.description,
          type: corridor.type as 'trajectory',
          points: corridor.waypoints?.map((wp: any) => ({
            lat: parseFloat(wp.latitude),
            lng: parseFloat(wp.longitude),
            address: wp.address
          })) || [],
          color: corridor.color,
          width: corridor.width,
          isActive: corridor.is_active,
          createdAt: new Date(corridor.created_at),
          updatedAt: new Date(corridor.updated_at)
        }))
        
        console.log(`✅ Loaded ${dbCorridors.length} corridors from database`)
        setCorridors(dbCorridors)
        if (onCorridorsChange) {
          onCorridorsChange(dbCorridors)
        }
      }
    } catch (error) {
      console.error('❌ Error loading corridors:', error)
      // Fallback to empty array if database fails
      setCorridors([])
    }
  }

  const loadGeofencesFromDatabase = async () => {
    try {
      console.log('📍 Loading geofences from database...')
      const response = await apiClient.get('/geofences')
      
      if (response.data && Array.isArray(response.data)) {
        const dbGeofences: Geofence[] = response.data.map(geofence => ({
          id: geofence.id,
          name: geofence.name,
          description: geofence.description,
          type: geofence.type,
          center: geofence.center_latitude && geofence.center_longitude ? {
            lat: parseFloat(geofence.center_latitude),
            lng: parseFloat(geofence.center_longitude)
          } : undefined,
          radius: geofence.radius ? parseFloat(geofence.radius) : undefined,
          bounds: geofence.bounds?.map((bound: any) => ({
            lat: parseFloat(bound.latitude),
            lng: parseFloat(bound.longitude)
          })) || [],
          color: geofence.color,
          fillColor: geofence.fill_color,
          isActive: geofence.is_active,
          alertOnEntry: geofence.alert_on_entry,
          alertOnExit: geofence.alert_on_exit,
          createdAt: new Date(geofence.created_at),
          updatedAt: new Date(geofence.updated_at)
        }))
        
        console.log(`✅ Loaded ${dbGeofences.length} geofences from database`)
        setGeofences(dbGeofences)
      }
    } catch (error) {
      console.error('❌ Error loading geofences:', error)
      // Fallback to empty array if database fails
      setGeofences([])
    }
  }

  const filteredCorridors = corridors.filter(corridor =>
    corridor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    corridor.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreateCorridor = () => {
    setIsCreating(true)
    setIsEditing(true)
    setEditForm({
      name: '',
      description: '',
      points: [],
      color: '#10B981',
      width: 5,
      isActive: true
    })
  }

  const handleEditCorridor = (corridor: Corridor) => {
    console.log('🔧 Starting corridor edit for:', corridor.name)
    // Clear any geofence editing first
    setIsEditingGeofence(false)
    setEditingGeofence(null)
    setSelectedGeofence(null)
    // Start corridor editing
    setSelectedCorridor(corridor)
    setIsEditing(true)
    setIsCreating(false)
    setEditForm(corridor)
  }

  // Handle corridor point updates from map editing
  const handleCorridorUpdate = async (corridorId: string, points: Array<{ lat: number, lng: number, address?: string }>) => {
    try {
      console.log(`💾 Updating corridor ${corridorId} with ${points.length} points`)
      
      // Update via API
      const response = await apiClient.put(`/corridors/${corridorId}`, {
        waypoints: points
      })
      
      if (response.data.success) {
        console.log('✅ Corridor updated successfully!')
        
        // Refresh corridors from database
        loadCorridorsFromDatabase()
        
        if (showMessage) {
          showMessage('Route updated successfully!', 'success')
        }
      } else {
        throw new Error(response.data.message || 'Failed to update corridor')
      }
      
    } catch (error) {
      console.error('❌ Error updating corridor:', error)
      if (showMessage) {
          showMessage(`Error updating route: ${error.message || error}`, 'error')
      }
    }
  }

  const handleSaveCorridor = () => {
    if (!editForm.name) return

    const corridorData: Corridor = {
      id: isCreating ? Date.now().toString() : selectedCorridor!.id,
      name: editForm.name!,
      description: editForm.description || '',
      type: 'trajectory',
      points: editForm.points || [],
      color: editForm.color || '#10B981',
      width: editForm.width || 5,
      isActive: editForm.isActive ?? true,
      createdAt: isCreating ? new Date() : selectedCorridor!.createdAt,
      updatedAt: new Date()
    }

    if (isCreating) {
      const newCorridors = [...corridors, corridorData]
      setCorridors(newCorridors)
      if (onCorridorsChange) onCorridorsChange(newCorridors)
    } else {
      const updatedCorridors = corridors.map(c => 
        c.id === corridorData.id ? corridorData : c
      )
      setCorridors(updatedCorridors)
      if (onCorridorsChange) onCorridorsChange(updatedCorridors)
    }

    setIsEditing(false)
    setIsCreating(false)
    setSelectedCorridor(null)
    setEditForm({})
  }

  const handleDeleteCorridor = async (corridorId: string) => {
    const confirmed = showConfirm ? await showConfirm('Are you sure you want to delete this corridor?') : confirm('Are you sure you want to delete this corridor?')
    if (confirmed) {
      try {
        console.log(`🗑️ Deleting corridor: ${corridorId}`)
        
        // Call API to delete from database
        const response = await apiClient.delete(`/corridors/${corridorId}`)
        
        if (response.data.success) {
          console.log('✅ Corridor deleted from database')
          
          // Update local state
      const updatedCorridors = corridors.filter(c => c.id !== corridorId)
      setCorridors(updatedCorridors)
      if (onCorridorsChange) onCorridorsChange(updatedCorridors)
      if (selectedCorridor?.id === corridorId) {
        setSelectedCorridor(null)
          }
          
          if (showMessage) {
            showMessage('Route deleted successfully!', 'success')
          }
        } else {
          throw new Error(response.data.message || 'Failed to delete corridor')
        }
        
      } catch (error) {
        console.error('❌ Error deleting corridor:', error)
        if (showMessage) {
          showMessage(`Error deleting route: ${error.message || error}`, 'error')
        }
      }
    }
  }

  // Handle geofence updates from map editing
  const handleGeofenceUpdate = async (geofenceId: string, geofenceData: any) => {
    try {
      console.log(`💾 Updating geofence ${geofenceId}`, geofenceData)
      
      // Update via API
      const response = await apiClient.put(`/geofences/${geofenceId}`, geofenceData)
      
      if (response.data.success) {
        console.log('✅ Geofence updated successfully!')
        
        // Refresh geofences from database
        loadGeofencesFromDatabase()
        
        if (showMessage) {
          showMessage('Geofence updated successfully!', 'success')
        }
      } else {
        throw new Error(response.data.message || 'Failed to update geofence')
      }
      
    } catch (error) {
      console.error('❌ Error updating geofence:', error)
        if (showMessage) {
          showMessage(`Error updating geofence: ${error.message || error}`, 'error')
        }
    }
  }

  // Handle geofence deletion
  const handleDeleteGeofence = async (geofenceId: string) => {
    const confirmed = showConfirm ? await showConfirm('Are you sure you want to delete this geofence?') : confirm('Are you sure you want to delete this geofence?')
    if (confirmed) {
      try {
        console.log(`🗑️ Deleting geofence: ${geofenceId}`)
        
        // Call API to delete from database
        const response = await apiClient.delete(`/geofences/${geofenceId}`)
        
        if (response.data.success) {
          console.log('✅ Geofence deleted from database')
          
          // Refresh geofences list
          loadGeofencesFromDatabase()
          
          // Clear selection if deleted geofence was selected
          if (selectedGeofence?.id === geofenceId) {
            setSelectedGeofence(null)
          }
          if (editingGeofence?.id === geofenceId) {
            setEditingGeofence(null)
            setIsEditingGeofence(false)
          }
          
          if (showMessage) {
            showMessage('Geofence deleted successfully!', 'success')
          }
        } else {
          throw new Error(response.data.message || 'Failed to delete geofence')
        }
        
      } catch (error) {
        console.error('❌ Error deleting geofence:', error)
        if (showMessage) {
          showMessage(`Error deleting geofence: ${error.message || error}`, 'error')
        }
      }
    }
  }

  const handleCorridorClick = (corridor: Corridor) => {
    // Toggle selection - if already selected, unselect it
    if (selectedCorridor?.id === corridor.id) {
      console.log(`🔄 Unselecting corridor: ${corridor.name}`)
      setSelectedCorridor(null)
      if (onCorridorSelect) onCorridorSelect(null)
    } else {
      console.log(`✅ Selecting corridor: ${corridor.name}`)
    setSelectedCorridor(corridor)
      
      // Trigger map centering
      setCenterOnCorridor(true)
      setTimeout(() => setCenterOnCorridor(false), 100) // Reset after triggering
      
    if (onCorridorSelect) {
      onCorridorSelect(corridor)
      }
    }
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setIsCreating(false)
    setSelectedCorridor(null)
    setEditForm({})
  }

  // Handle drawing mode
  const handleStartDrawing = (type: 'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point' | 'geofence') => {
    console.log(`🎨 Starting drawing: ${type}`)
    setPendingCreation(null)
    
    // Clear any existing editing states when starting new drawing
    if (isEditingGeofence) {
      console.log('🧹 Clearing geofence editing mode to start new drawing')
      setIsEditingGeofence(false)
      setEditingGeofence(null)
      setSelectedGeofence(null)
    }
    
    if (isEditing) {
      console.log('🧹 Clearing corridor editing mode to start new drawing')
      setIsEditing(false)
      setSelectedCorridor(null)
    }
    
    if (type === 'geofence') {
      // For generic geofence, don't set specific type yet - let user choose in panel
      setDrawingType('geofence' as any)  // Use geofence as a special type
      setIsDrawingMode(true)
      console.log('📍 Opening geofence creation panel with shape selector')
    } else {
      setDrawingType(type as 'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point')
    setIsDrawingMode(true)
      
      // For specific geofence tools, set the type
      if (type !== 'trajectory') {
        console.log(`📍 Setting geofence type to: ${type}`)
      }
    }
    
    if (onStartDrawing) {
      onStartDrawing(type === 'geofence' ? 'circle' : type)
    }
  }

  // Handle drawing completion
  const handleDrawingComplete = (points: Array<{ lat: number, lng: number }>) => {
    console.log(`✅ Drawing completed with ${points.length} points`)
    setIsDrawingMode(false)
    setPendingCreation(null)
    
    // Add a small delay to ensure database transaction is complete
    setTimeout(() => {
      if (drawingType === 'trajectory') {
        console.log('🔄 Refreshing corridor list after route creation...')
    loadCorridorsFromDatabase()
      } else {
        console.log('🔄 Refreshing geofence list after geofence creation...')
        loadGeofencesFromDatabase()
      }
    }, 500) // 500ms delay
  }


  return (
    <div className="h-full flex bg-white">
      {/* Left Panel - Controls */}
      <div className="w-80 flex flex-col border-r border-gray-200">
        {/* Header with Tabs */}
        <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <RouteIcon className="text-blue-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">{t('corridor_management_title')}</h2>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-4 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('trajectories')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === 'trajectories'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <RouteIcon size={14} className="inline mr-1" /> {t('trajectories')}
          </button>
          <button
            onClick={() => setActiveTab('geofences')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === 'geofences'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <MapPin size={14} className="inline mr-1" /> {t('geofences')}
          </button>
        </div>

        {/* Drawing Tools */}
        {activeTab === 'trajectories' ? (
          <div className="flex items-center space-x-2 mb-4">
            <button
              onClick={() => handleStartDrawing('trajectory')}
              className="flex items-center space-x-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <RouteIcon size={16} />
              <span>{t('draw_route')}</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2 mb-4">
              <button
              onClick={() => handleStartDrawing('geofence')}
                className="flex items-center space-x-2 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
              <MapPin size={16} />
              <span>{t('add_geofence')}</span>
              </button>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isEditing ? (
          /* Edit Form */
          <div className="p-4 h-full overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-800">
                  {isCreating ? t('create_new_corridor') : t('edit_corridor')}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveCorridor}
                    className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    <Save size={14} />
                    <span>{t('save')}</span>
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="flex items-center space-x-1 bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                  >
                    <X size={14} />
                    <span>{t('cancel')}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('corridor_name')}
                </label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('corridor_name_placeholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('description')}
                </label>
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder={t('description_placeholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('color')}
                  </label>
                  <input
                    type="color"
                    value={editForm.color || '#10B981'}
                    onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('width')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={editForm.width || 5}
                    onChange={(e) => setEditForm({ ...editForm, width: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editForm.isActive ?? true}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  {t('active_corridor')}
                </label>
              </div>
            </div>
          </div>
        ) : (
          /* Content based on active tab */
          <div className="h-full overflow-y-auto">
            {activeTab === 'trajectories' ? (
              /* Trajectory List */
              filteredCorridors.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <RouteIcon size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">{t('no_trajectories')}</p>
                  <p className="text-sm">Click "Draw Route" to create your first trajectory</p>
                </div>
              ) : (
                <div className="p-2">
                  {filteredCorridors.map((corridor) => (
                    <div
                      key={corridor.id}
                      className={`p-4 mb-2 rounded-lg border cursor-pointer transition-all ${
                        selectedCorridor?.id === corridor.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleCorridorClick(corridor)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <RouteIcon size={16} className="text-green-600" />
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: corridor.color }}
                            />
                            <h3 className="font-medium text-gray-800">{corridor.name}</h3>
                            {!corridor.isActive && (
                              <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                                {t('inactive')}
                              </span>
                            )}
                          </div>
                          {corridor.description && (
                            <p className="text-sm text-gray-600 mb-2">{corridor.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="flex items-center space-x-1">
                              <MapPin size={12} />
                              <span>{corridor.points.length} points</span>
                            </span>
                            <span>Width: {corridor.width}px</span>
                            <span>Updated: {corridor.updatedAt.toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditCorridor(corridor)
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteCorridor(corridor.id)
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              /* Geofence List */
              geofences.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Circle size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">{t('no_geofences')}</p>
                  <p className="text-sm">Use the drawing tools to create geofences</p>
                </div>
              ) : (
                <div className="p-2">
                  {geofences.map((geofence) => (
                    <div
                      key={geofence.id}
                      className={`p-4 mb-2 rounded-lg border cursor-pointer transition-all ${
                        selectedGeofence?.id === geofence.id || editingGeofence?.id === geofence.id
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        // Toggle selection - if already selected, unselect it
                        if (selectedGeofence?.id === geofence.id) {
                          console.log(`🔄 Unselecting geofence: ${geofence.name}`)
                          setSelectedGeofence(null)
                          if (onGeofenceSelect) onGeofenceSelect(null)
                          // Clear any editing states when unselecting
                          console.log('🧹 Clearing editing states on unselect')
                          setIsEditingGeofence(false)
                          setEditingGeofence(null)
                        } else {
                          console.log(`✅ Selecting geofence: ${geofence.name}`)
                          console.log('📋 Current editing states:', { isEditingGeofence, editingGeofence: editingGeofence?.name })
                          setSelectedGeofence(geofence)
                          
                          // Trigger map centering (same as corridors)
                          setCenterOnGeofence(true)
                          setTimeout(() => setCenterOnGeofence(false), 100) // Reset after triggering
                          
                          if (onGeofenceSelect) onGeofenceSelect(geofence)
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            {geofence.type === 'circle' && <Circle size={16} className="text-red-600" />}
                            {geofence.type === 'square' && <Square size={16} className="text-orange-600" />}
                            {geofence.type === 'triangle' && <Triangle size={16} className="text-purple-600" />}
                            {geofence.type === 'polygon' && <MapPin size={16} className="text-indigo-600" />}
                            {geofence.type === 'point' && <MapPin size={16} className="text-blue-600" />}
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: geofence.color }}
                            />
                            <h3 className="font-medium text-gray-800">{geofence.name}</h3>
                            {editingGeofence?.id === geofence.id && (
                              <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded font-medium">
                                <Pencil size={12} className="inline mr-1" /> {t('editing')}
                              </span>
                            )}
                            {!geofence.isActive && (
                              <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                                {t('inactive')}
                              </span>
                            )}
                          </div>
                          {geofence.description && (
                            <p className="text-sm text-gray-600 mb-2">{geofence.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="capitalize">{geofence.type}</span>
                            {geofence.radius && <span>Radius: {geofence.radius}m</span>}
                            <div className="flex space-x-2">
                              {geofence.alertOnEntry && <span className="bg-green-100 text-green-700 px-2 py-1 rounded">{t('entry')}</span>}
                              {geofence.alertOnExit && <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{t('exit')}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              console.log('🔧 Starting geofence edit for:', geofence.name)
                              console.log('📋 Geofence data:', geofence)
                              console.log('🔍 Current states before edit:', {
                                selectedGeofence: selectedGeofence?.name,
                                isEditingGeofence,
                                editingGeofence: editingGeofence?.name,
                                drawingType
                              })
                              
                              // Clear any corridor editing first
                              setIsEditing(false)
                              setIsCreating(false)
                              setSelectedCorridor(null)
                              // Clear any drawing mode
                              setIsDrawingMode(false)
                              
                              // Clear any previous editing states to prevent conflicts
                              console.log('🧹 Clearing previous editing states')
                              setIsEditingGeofence(false)
                              setEditingGeofence(null)
                              
                              // Small delay to ensure state is cleared before setting new states
                              setTimeout(() => {
                                console.log('⏰ Setting new editing states after delay')
                                // Start geofence editing
                                console.log('🔄 Setting editingGeofence to:', geofence)
                                setEditingGeofence(geofence)
                                console.log('🔄 Setting isEditingGeofence to: true')
                                setIsEditingGeofence(true)
                                console.log('🔄 Setting selectedGeofence to:', geofence.name)
                                setSelectedGeofence(geofence)
                                // IMPORTANT: Set drawingType to 'geofence' so Place Type and Purpose selectors are visible
                                console.log('🔄 Setting drawingType to geofence (was', drawingType, ') to show Place Type/Purpose selectors')
                                setDrawingType('geofence')
                              }, 50)
                            }}
                            className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteGeofence(geofence.id)
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
        </div>
      </div>

      {/* Right Panel - Map */}
      <div className="flex-1 flex flex-col">
        <CorridorMap
          corridors={corridors}
          geofences={geofences}
          selectedCorridor={selectedCorridor}
          selectedGeofence={selectedGeofence}
          isDrawingMode={isDrawingMode}
          drawingType={drawingType}
          onDrawingComplete={handleDrawingComplete}
          isEditingMode={isEditing && !isCreating && !isEditingGeofence}
          editingCorridor={isEditing && !isCreating && !isEditingGeofence ? selectedCorridor : null}
          onCorridorUpdate={handleCorridorUpdate}
          centerOnCorridor={centerOnCorridor}
          centerOnGeofence={centerOnGeofence}
          isEditingGeofence={isEditingGeofence}
          editingGeofence={editingGeofence}
          onGeofenceUpdate={handleGeofenceUpdate}
          showMessage={showMessage}
          showConfirm={showConfirm}
          initialDrawingPoint={pendingCreation?.point}
          initialDrawingZoom={pendingCreation?.zoom}
          initialReferenceTrajectory={pendingCreation?.referenceTrajectory}
          initialVehicle={pendingCreation ? { ...pendingCreation.vehicle, label: pendingCreation.vehicleLabel, dateRange: pendingCreation.dateRange } : undefined}
          initialDrawingRequestId={pendingCreation?.requestId}
          initialDrawingLabel={pendingCreation?.vehicleLabel}
        />
      </div>
    </div>
  )
}

export default CorridorManager
export type { Corridor, Geofence }
